import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getDatabase, ref, set, get, update, remove } from 'firebase/database';
import { saveToCloudWithGoogle } from '../config/firebase';

const AuthContext = createContext();

// Navigation callback - set by components that need to navigate after login
let onLoginSuccessCallback = null;

export function setOnLoginSuccess(callback) {
  onLoginSuccessCallback = callback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = getAuth();
  const database = getDatabase();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Load username + usernameLastUpdated from DB
        let username = null;
        let usernameLastUpdated = null;
        try {
          const snap = await get(ref(database, `users/${firebaseUser.uid}`));
          const data = snap.val() || {};
          username = data.username || null;
          usernameLastUpdated = data.usernameLastUpdated || null;
        } catch (_) {}

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          username,
          usernameLastUpdated,
        });

        // Update user record in database
        await updateUserRecord(firebaseUser);
      } else {
        // User is signed out
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  /**
   * Update or create user record in /users/ node
   */
  const updateUserRecord = async (firebaseUser) => {
    try {
      const userRef = ref(database, `users/${firebaseUser.uid}`);
      const snapshot = await get(userRef);
      const existingData = snapshot.val();

      await set(userRef, {
        // Preserve any existing fields (galleryToSAccepted, publishAnonymously, etc.)
        ...existingData,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        createdAt: existingData?.createdAt || new Date().toISOString(),
        lastLogin: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to update user record:', error);
    }
  };

  /**
   * Sign in with Google
   * Returns: { success, needsSync, needsMigration, hasConflict, localData, cloudData }
   */
  const login = async () => {
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      // Check for existing collections and potential conflicts
      const syncStatus = await checkCollectionStatus(firebaseUser.uid);

      // Call the login success callback if set (for navigation)
      if (onLoginSuccessCallback) {
        onLoginSuccessCallback();
      }

      return {
        success: true,
        user: firebaseUser,
        ...syncStatus
      };
    } catch (error) {
      console.error('Login failed:', error);

      // Storage partitioning issue (Safari, Brave, in-app browsers)
      const isStorageError =
        error.code === 'auth/internal-error' ||
        (error.message || '').toLowerCase().includes('missing initial state') ||
        (error.message || '').toLowerCase().includes('sessionstorage');

      return {
        success: false,
        error: isStorageError
          ? 'Sign-in blocked by your browser\'s privacy settings. Please open markersync.com directly in Chrome or Safari (not inside another app) and try again.'
          : error.message
      };
    }
  };

  /**
   * Check collection status after login
   * Determines if sync, migration, or conflict resolution is needed
   */
  const checkCollectionStatus = async (googleUid) => {
    const localCollection = JSON.parse(localStorage.getItem('markerCollection') || '[]');
    const localTimestamp = localStorage.getItem('markerCollectionTimestamp');
    const backupKey = localStorage.getItem('markerSync_backupKey');

    // Load Google account collection
    let cloudCollection = null;
    let cloudTimestamp = null;

    try {
      const collectionRef = ref(database, `collections/${googleUid}`);
      const snapshot = await get(collectionRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        cloudCollection = data.markers || [];
        cloudTimestamp = data.updatedAt;

        // Handle Firebase array-to-object conversion
        if (!Array.isArray(cloudCollection) && typeof cloudCollection === 'object') {
          cloudCollection = Object.values(cloudCollection);
        }
      }
    } catch (error) {
      console.error('Failed to load cloud collection:', error);
    }

    // Scenario 1: User has backup key (needs migration)
    if (backupKey) {
      // Load backup key collection
      let backupCollection = [];
      try {
        const backupRef = ref(database, `collections/${backupKey}`);
        const backupSnapshot = await get(backupRef);
        if (backupSnapshot.exists()) {
          const backupData = backupSnapshot.val();
          backupCollection = backupData.markers || [];
          if (!Array.isArray(backupCollection) && typeof backupCollection === 'object') {
            backupCollection = Object.values(backupCollection);
          }
        }
      } catch (error) {
        console.error('Failed to load backup collection:', error);
      }

      return {
        needsMigration: true,
        backupKey: backupKey,
        backupCollection: backupCollection,
        backupCount: backupCollection.length,
        cloudCollection: cloudCollection,
        cloudCount: cloudCollection?.length || 0
      };
    }

    // Scenario 2: User has local collection but no cloud collection (needs sync)
    if (localCollection.length > 0 && (!cloudCollection || cloudCollection.length === 0)) {
      return {
        needsSync: true,
        localCollection: localCollection,
        localCount: localCollection.length
      };
    }

    // Scenario 3: Both local and cloud have data (potential conflict)
    if (localCollection.length > 0 && cloudCollection && cloudCollection.length > 0) {
      // Check if they're different
      const localIds = new Set(localCollection.map(m => m.id || `${m.brand}-${m.code}`));
      const cloudIds = new Set(cloudCollection.map(m => m.id || `${m.brand}-${m.code}`));

      const areSame = localIds.size === cloudIds.size &&
        [...localIds].every(id => cloudIds.has(id));

      if (!areSame) {
        return {
          hasConflict: true,
          localCollection: localCollection,
          localCount: localCollection.length,
          localTimestamp: localTimestamp ? new Date(parseInt(localTimestamp)).toISOString() : null,
          cloudCollection: cloudCollection,
          cloudCount: cloudCollection.length,
          cloudTimestamp: cloudTimestamp
        };
      }
    }

    // Scenario 4: No issues - either new user or collections match
    return {
      needsSync: false,
      needsMigration: false,
      hasConflict: false,
      cloudCollection: cloudCollection,
      cloudCount: cloudCollection?.length || 0
    };
  };

  /**
   * Sign out
   */
  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Migrate backup key collection to Google account
   */
  const migrateFromBackupKey = async (backupKey, markers) => {
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    try {
      await saveToCloudWithGoogle(markers, user.uid, { migratedFrom: backupKey });

      // Remove backup key from localStorage
      localStorage.removeItem('markerSync_backupKey');
      localStorage.removeItem('markerSync_cloudOptIn');

      return {
        success: true,
        migratedCount: markers.length,
        migratedFrom: backupKey
      };
    } catch (error) {
      console.error('Migration failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Sync local collection to Google account (first time sync)
   */
  const syncLocalToCloud = async (markers) => {
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    try {
      await saveToCloudWithGoogle(markers, user.uid);

      return { success: true, syncedCount: markers.length };
    } catch (error) {
      console.error('Sync failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Set or update the user's unique username
   */
  const setUsername = async (newUsername) => {
    if (!user) return { success: false, error: 'Not logged in' };

    const normalized = newUsername.trim().toLowerCase();
    if (!/^[a-z0-9_]{3,20}$/.test(normalized)) {
      return { success: false, error: 'Invalid username format' };
    }

    try {
      // Get current username + last updated to enforce cooldown
      const userSnap = await get(ref(database, `users/${user.uid}`));
      const currentData = userSnap.val() || {};
      const currentUsername = currentData.username || null;
      const lastUpdated = currentData.usernameLastUpdated || null;

      if (currentUsername === normalized) {
        return { success: false, error: 'That is already your username' };
      }

      // Enforce 10-day cooldown for changes (not for first-time set)
      if (currentUsername && lastUpdated) {
        const daysSince = (Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 10) {
          const daysLeft = Math.ceil(10 - daysSince);
          return {
            success: false,
            error: `You can change your username again in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`,
            cooldown: true,
          };
        }
      }

      // Claim new username + update user record
      const now = new Date().toISOString();
      await update(ref(database), {
        [`usernames/${normalized}`]: user.uid,
        [`users/${user.uid}/username`]: normalized,
        [`users/${user.uid}/usernameLastUpdated`]: now,
      });

      // Migrate public profile from old username path to new one
      if (currentUsername) {
        try {
          const profileSnap = await get(ref(database, `publicProfiles/${currentUsername}`));
          if (profileSnap.exists()) {
            const profileData = { ...profileSnap.val(), username: normalized };
            await update(ref(database), {
              [`publicProfiles/${normalized}`]: profileData,
              [`publicProfiles/${currentUsername}`]: null,
            });
          }
        } catch (_) {
          // Non-fatal: profile migration failed but username change succeeded
        }
      }

      // Release old username slot (separate write — delete requires its own call)
      if (currentUsername) {
        await remove(ref(database, `usernames/${currentUsername}`));
      }
      setUser(prev => ({ ...prev, username: normalized, usernameLastUpdated: now }));
      return { success: true, username: normalized };
    } catch (error) {
      // Permission denied = username already taken
      if (error.code === 'PERMISSION_DENIED') {
        return { success: false, error: 'Username already taken' };
      }
      return { success: false, error: error.message };
    }
  };

  /**
   * Resolve collection conflict
   * @param {string} choice - 'local' or 'cloud'
   */
  const resolveConflict = async (choice, localCollection, cloudCollection) => {
    if (!user) {
      return { success: false, error: 'Not logged in' };
    }

    try {
      if (choice === 'local') {
        await saveToCloudWithGoogle(localCollection, user.uid);

        // Update localStorage to match
        localStorage.setItem('markerCollection', JSON.stringify(localCollection));
        localStorage.setItem('markerCollectionTimestamp', Date.now().toString());

        return { success: true, collection: localCollection };
      } else {
        // Download cloud to local
        localStorage.setItem('markerCollection', JSON.stringify(cloudCollection));
        localStorage.setItem('markerCollectionTimestamp', Date.now().toString());

        return { success: true, collection: cloudCollection };
      }
    } catch (error) {
      console.error('Conflict resolution failed:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    migrateFromBackupKey,
    syncLocalToCloud,
    resolveConflict,
    checkCollectionStatus,
    setUsername,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
